import type { IJwt } from './types'
import type { LoginMutation } from '~/shared/api'
import {
  useLoginByCodeMutation,
  useLoginMutation,
  useRefreshTokenMutation,
} from '~/shared/api'
import { decodeJwt, permissions } from '~/shared/lib'
import { usePushNotifications } from '~/shared/lib/firebase'
import { getAuthCookieOptions } from './cookies'
import { useLoginCode } from './loginCode'
import { resolveSafePostAuthRedirect } from './pinGate'
import { useAccountStore } from './store/account'

export const EXPIRATION_BUFFER = 30 * 1000

let refreshInFlight: Promise<boolean> | null = null

export type LoginByCodeResult =
  | { ok: true }
  | {
    ok: false
    reason: 'no_refresh_token' | 'wrong_code' | 'session_expired' | 'code_not_set' | 'unknown'
  }

export interface NavigateAfterAuthOptions {
  redirect?: string
  offerPinSetup?: boolean
}

function getGraphqlErrorMessage(error: unknown) {
  if (!error || typeof error !== 'object') {
    return ''
  }

  if ('graphQLErrors' in error && Array.isArray(error.graphQLErrors)) {
    return error.graphQLErrors
      .map(item => (item && typeof item === 'object' && 'message' in item && typeof item.message === 'string')
        ? item.message
        : '')
      .filter(Boolean)
      .join(' ')
  }

  if ('message' in error && typeof error.message === 'string') {
    return error.message
  }

  return ''
}

function getLoginByCodeFailureReason(message: string): Exclude<LoginByCodeResult, { ok: true }>['reason'] {
  const normalized = message.toLowerCase()

  if (normalized.includes('сессия истекла') || normalized.includes('session expired')) {
    return 'session_expired'
  }

  if (normalized.includes('неверный код') || normalized.includes('invalid code') || normalized.includes('wrong code')) {
    return 'wrong_code'
  }

  if (normalized.includes('код не установлен') || normalized.includes('code not set')) {
    return 'code_not_set'
  }

  return 'unknown'
}

export function useAuth() {
  const accessToken = useCookie('access_token', getAuthCookieOptions(60 * 60 * 24 * 7))
  const refreshToken = useCookie('refresh_token', getAuthCookieOptions(60 * 60 * 24 * 30))
  const expiresIn = useCookie('expires_in', getAuthCookieOptions(60 * 60 * 24 * 7))

  const router = useRouter()
  const accountStore = useAccountStore()
  const {
    setAccountFromJwt,
    reset: resetAccount,
    resetIdentifier: resetAccountIdentifier,
  } = accountStore
  const { mutate: loginMutation } = useLoginMutation()
  const { mutate: refreshTokenMutation } = useRefreshTokenMutation()
  const { mutate: loginByCodeMutation } = useLoginByCodeMutation()
  const { onLogin } = useApollo()
  const {
    isLoginCodeEnabled,
    isLoginCodeStatusKnown,
    fetchLoginCodeStatus,
    clearClientPinState,
  } = useLoginCode()

  const { requestPermission, fcmToken, registerUserToken } = usePushNotifications()

  const isAuthenticated = computed(() => Boolean(accessToken.value || refreshToken.value))

  function getCurrentUserId() {
    if (accountStore.data.id) {
      return accountStore.data.id
    }

    if (!accessToken.value) {
      return ''
    }

    const jwt = decodeJwt<IJwt>(accessToken.value)

    if (jwt?.sub) {
      accountStore.data.id = jwt.sub
      return jwt.sub
    }

    return ''
  }

  function shouldRequirePinUnlock() {
    return isLoginCodeEnabled() && Boolean(refreshToken.value) && isTokenExpired()
  }

  async function syncLoginCodeStatus() {
    if (!accessToken.value) {
      return false
    }

    return fetchLoginCodeStatus()
  }

  function setLoginData(data?: LoginMutation['login']) {
    if (data) {
      let accessTokenExpirationTime
        = Date.now() + Number(data.expires_in ?? 0) * 1000

      if (!data.expires_in || Number(data.expires_in) <= 0) {
        const jwt = data.access_token
          ? decodeJwt<IJwt>(data.access_token)
          : null

        if (jwt?.exp) {
          accessTokenExpirationTime = jwt.exp * 1000
        }
      }

      expiresIn.value = accessTokenExpirationTime.toString()

      if (data.refresh_token) {
        refreshToken.value = data.refresh_token
      }

      accessToken.value = data.access_token

      setAccountFromJwt(data.access_token)
      resetAccountIdentifier()
    }
  }

  function clearLoginData() {
    clearClientPinState(getCurrentUserId() || undefined)
    resetAccount()

    accessToken.value = null
    refreshToken.value = null
    expiresIn.value = '0'
  }

  function isTokenExpired() {
    if (!accessToken.value) {
      return true
    }

    const expirationTime = Number(expiresIn.value)

    if (
      expiresIn.value
      && expiresIn.value !== '0'
      && !Number.isNaN(expirationTime)
      && expirationTime > 0
    ) {
      return Date.now() >= expirationTime - EXPIRATION_BUFFER
    }

    const jwt = decodeJwt<IJwt>(accessToken.value)

    if (jwt?.exp) {
      return Date.now() >= jwt.exp * 1000 - EXPIRATION_BUFFER
    }

    return true
  }

  async function syncLoginCodeFlagIfNeeded() {
    if (isLoginCodeStatusKnown() || !refreshToken.value || !accessToken.value) {
      return
    }

    await syncLoginCodeStatus()
  }

  async function requestPushPermissionAndRegisterToken() {
    try {
      const userId = getCurrentUserId()

      if (!userId) {
        console.warn('Невозможно получить ID пользователя для регистрации токена уведомлений')
        return
      }

      if (!fcmToken.value) {
        await requestPermission()
      }

      if (fcmToken.value && fcmToken.value !== 'local-only-mode') {
        await registerUserToken(userId, fcmToken.value)
      }
    }
    catch (error) {
      console.error('Ошибка при регистрации токена:', error)
    }
  }

  async function loginByCode(code: string): Promise<LoginByCodeResult> {
    const currentRefreshToken = refreshToken.value

    if (!currentRefreshToken) {
      return { ok: false, reason: 'no_refresh_token' }
    }

    try {
      const response = await loginByCodeMutation({
        refreshToken: currentRefreshToken,
        code,
      })

      if (response?.errors?.length) {
        const message = response.errors
          .map(error => error.message)
          .filter(Boolean)
          .join(' ')

        return {
          ok: false,
          reason: getLoginByCodeFailureReason(message),
        }
      }

      if (!response?.data?.loginByCode) {
        return { ok: false, reason: 'unknown' }
      }

      setLoginData(response.data.loginByCode)
      onLogin(response.data.loginByCode.access_token ?? '')

      return { ok: true }
    }
    catch (error) {
      return {
        ok: false,
        reason: getLoginByCodeFailureReason(getGraphqlErrorMessage(error)),
      }
    }
  }

  async function login(
    username: string,
    password: string,
    method: 'phone' | 'email',
  ) {
    const response = await loginMutation(
      method === 'phone'
        ? { phone: username, password }
        : { email: username, password },
    )

    setLoginData(response?.data?.login)
    onLogin(response?.data?.login.access_token ?? '')

    if (response?.data?.login) {
      setTimeout(() => {
        requestPushPermissionAndRegisterToken()
      }, 500)

      await syncLoginCodeStatus()
    }
  }

  async function refresh() {
    if (isLoginCodeEnabled()) {
      return false
    }

    if (!refreshToken.value) {
      return false
    }

    if (refreshInFlight) {
      return refreshInFlight
    }

    refreshInFlight = (async () => {
      try {
        const currentRefreshToken = refreshToken.value

        if (!currentRefreshToken) {
          return false
        }

        const response = await refreshTokenMutation({
          refreshToken: currentRefreshToken,
        })

        if (!response?.data?.refreshToken) {
          return false
        }

        setLoginData(response.data.refreshToken)

        if (response.data.refreshToken.access_token) {
          onLogin(response.data.refreshToken.access_token)
        }

        return true
      }
      catch {
        return false
      }
      finally {
        refreshInFlight = null
      }
    })()

    return refreshInFlight
  }

  async function ensureValidAccessToken(options?: { allowRefresh?: boolean }) {
    if (!accessToken.value && !refreshToken.value) {
      return false
    }

    if (!isTokenExpired()) {
      return true
    }

    if (isLoginCodeEnabled()) {
      return false
    }

    if (options?.allowRefresh === false) {
      return false
    }

    const isRefreshed = await refresh()

    if (!isRefreshed) {
      clearLoginData()
      return false
    }

    return true
  }

  async function logout(options?: {
    redirect?: string
    query?: Record<string, string | undefined>
  }) {
    clearLoginData()
    router.push({
      name: permissions.AUTH.SIGNIN.PERMISSION,
      query: {
        redirect: options?.redirect,
        ...options?.query,
      },
    })
  }

  async function navigateAfterAuth(options?: NavigateAfterAuthOptions) {
    const redirect = resolveSafePostAuthRedirect(options?.redirect)
    const offerPinSetup = options?.offerPinSetup ?? true
    const hasPin = await syncLoginCodeStatus()

    if (!hasPin && offerPinSetup) {
      return navigateTo({
        name: permissions.AUTH.PIN.SETUP.PERMISSION,
        query: redirect ? { redirect } : undefined,
      })
    }

    return navigateTo(
      redirect ?? { name: permissions.WORKSPACE.PERMISSION },
    )
  }

  return {
    accessToken,
    refreshToken,
    expiresIn,
    isTokenExpired,
    getCurrentUserId,
    login,
    loginByCode,
    logout,
    refresh,
    ensureValidAccessToken,
    isAuthenticated,
    shouldRequirePinUnlock,
    syncLoginCodeStatus,
    syncLoginCodeFlagIfNeeded,
    navigateAfterAuth,
    requestPushPermissionAndRegisterToken,
  }
}
